import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import EntityLogo from "@/components/EntityLogo";
import { Shield, Search, MapPin } from "lucide-react";

const PROVINCES = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];

export default function TeamsDirectory() {
  const [teams, setTeams] = useState(null);
  const [err, setErr] = useState(false);
  const [q, setQ] = useState("");
  const [prov, setProv] = useState("");

  const load = () => {
    setErr(false); setTeams(null);
    api.get("/live/teams").then(({ data }) => setTeams(data || [])).catch(() => { setErr(true); setTeams([]); });
  };
  useEffect(load, []);

  const divisions = useMemo(() => {
    const set = new Set((teams || []).map((t) => t.age_division).filter(Boolean));
    return Array.from(set).sort();
  }, [teams]);
  const [div, setDiv] = useState("");

  const filtered = useMemo(() => {
    if (!teams) return [];
    const query = q.trim().toLowerCase();
    return teams.filter((t) =>
      (!prov || t.province === prov) &&
      (!div || t.age_division === div) &&
      (!query || `${t.name} ${t.province || ""}`.toLowerCase().includes(query))
    );
  }, [teams, q, prov, div]);

  return (
    <PublicLayout>
      <section className="bg-[#0A192F] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="label-overline text-white/50">Clubs &amp; Teams</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter text-white mt-2">Search Teams</h1>
          <p className="text-white/60 mt-3 max-w-2xl">Find teams across CBR Nation. Search by name, filter by province or age division.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-3xl">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="team-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search teams…"
                className="w-full bg-white/10 border border-white/15 text-white placeholder-white/40 rounded-sm pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#E63946]" />
            </div>
            <select data-testid="team-province-filter" value={prov} onChange={(e) => setProv(e.target.value)}
              className="bg-white/10 border border-white/15 text-white rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-[#E63946]">
              <option value="" className="text-black">All Provinces</option>
              {PROVINCES.map((p) => <option key={p} value={p} className="text-black">{p}</option>)}
            </select>
            <select data-testid="team-division-filter" value={div} onChange={(e) => setDiv(e.target.value)}
              className="bg-white/10 border border-white/15 text-white rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-[#E63946]">
              <option value="" className="text-black">All Divisions</option>
              {divisions.map((d) => <option key={d} value={d} className="text-black">{d}</option>)}
            </select>
          </div>
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-10 min-h-[40vh]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {teams === null ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-loading">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-24 bg-white border border-slate-200 rounded-sm animate-pulse" />)}
            </div>
          ) : err ? (
            <div className="text-center py-20" data-testid="team-error">
              <Shield className="w-10 h-10 text-slate-300 mx-auto" />
              <p className="text-slate-500 mt-3 text-sm">We couldn't load teams right now.</p>
              <button onClick={load} className="mt-3 text-sm font-semibold text-[#E63946] uppercase tracking-wider">Retry</button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20" data-testid="team-empty">
              <Shield className="w-10 h-10 text-slate-300 mx-auto" />
              <p className="text-slate-500 mt-3 text-sm">No teams match your search yet.</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-slate-500 mb-4" data-testid="team-count">{filtered.length} team{filtered.length !== 1 ? "s" : ""}</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-grid">
                {filtered.map((t) => (
                  <Link key={t.id} to={`/live/teams/${t.slug}`} data-testid={`team-card-${t.slug}`}
                    className="group bg-white border border-slate-200 rounded-sm p-4 flex items-center gap-4 hover:border-[#E63946] hover:shadow-sm transition-all">
                    <EntityLogo src={t.effective_logo_url || t.logo_url} alt={t.name} size="w-14 h-14" textSize="text-sm" />
                    <div className="min-w-0">
                      <div className="font-display text-lg uppercase tracking-tight text-[#0A192F] truncate group-hover:text-[#E63946]">{t.name}</div>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5 flex-wrap">
                        {t.age_division && <span className="font-semibold text-[#0A192F]">{t.age_division}</span>}
                        {t.province && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{t.province}</span>}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
